import { useCallback, useEffect, useState } from 'react';
import alertService from '../../../services/AlertService';
import { handleError } from '../../../utils/apiUtils';
import { getTasklistInstance } from '../../steps';
import { Step } from '../../steps';
import { forceCompleteFeedbackTask } from '../api/forceCompleteFeedbackTask';
import { removeFeedbackResponseUser } from '../api/removeFeedbackResponseUser';
import { resendFeedbackUser as resendFeedbackUserRequest } from '../api/resendFeedbackUser';

export type RequestEntry = {
  email: string;
  firstName: string;
  status: '' | 'not_started' | 'in_progress' | 'complete';
  responseId: string;
};

const emptyRequestEntry = (): RequestEntry => ({
  email: '',
  firstName: '',
  status: '',
  responseId: '',
});

export function useFeedbackViewer(taskListInstanceId?: string, taskId?: string, userId?: string) {
  const [step, setStep] = useState<{ id?: string; data?: Step; isLoading: boolean; isError: boolean }>({
    isLoading: true,
    isError: false,
  });
  const [requests, setRequests] = useState<RequestEntry[]>([]);

  const clearRequestSlot = useCallback((index: number) => {
    setRequests(prev => prev.map((r, i) => (i === index ? emptyRequestEntry() : r)));
  }, []);

  useEffect(() => {
    if (!userId || !taskListInstanceId || !taskId) return;
    getTasklistInstance(taskListInstanceId, true)
      .then(course => {
        if (!course) {
          setStep(prev => ({ ...prev, isError: true, isLoading: false }));
          return;
        }
        const currentTask = course.tasks.find(t => t.id.id === taskId);
        if (!currentTask?.task || !('feedbackResponses' in currentTask.task)) {
          setStep(prev => ({ ...prev, isError: true, isLoading: false }));
          return;
        }
        const ft = currentTask.task;
        setStep(prev => ({
          ...prev,
          id: course.id,
          data: currentTask,
          isLoading: false,
          isError: false,
        }));
        setRequests(
          Array.from({ length: ft.numEmailsToGather }, (_, i) => {
            const r = ft.feedbackResponses[i];
            return r ? ({ ...r } as RequestEntry) : emptyRequestEntry();
          })
        );
      })
      .catch(() => {
        setStep(prev => ({ ...prev, isError: true, isLoading: false }));
      });
  }, [userId, taskListInstanceId, taskId]);

  const resetFeedbackUser = useCallback(
    (entry: RequestEntry, index: number) => {
      if (!userId || !step?.id || !step.data?.task) return;
      removeFeedbackResponseUser(step.id, step.data, entry.email, userId)
        .then(() => {
          alertService.show('Invitation cleared', 'success');
          clearRequestSlot(index);
        })
        .catch(handleError);
    },
    [userId, step, clearRequestSlot]
  );

  const resendFeedbackUser = useCallback(
    (entry: RequestEntry) => {
      if (!userId || !step?.id || !step.data?.task) return;
      resendFeedbackUserRequest(step.id, step.data, entry.email, entry.firstName, userId)
        .then(() => alertService.show('Invitation resent', 'success'))
        .catch(handleError);
    },
    [userId, step]
  );

  const markFeedbackTaskComplete = useCallback(() => {
    if (!userId || !step?.id || !step.data) return;
    forceCompleteFeedbackTask(step.id, step.data.id, userId)
      .then(() => {
        setStep(prev => {
          if (!prev.data) return prev;
          return { ...prev, data: { ...prev.data, status: 'complete' } };
        });
        alertService.show('Step completed', 'success');
      })
      .catch(handleError);
  }, [userId, step, setStep]);

  return {
    step,
    setStep,
    requests,
    setRequests,
    clearRequestSlot,
    resetFeedbackUser,
    resendFeedbackUser,
    markFeedbackTaskComplete,
  };
}
